












import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import WebRTCProvider, { WebRTC } from '@/webrtc'
import {
  WebRTCEventTypes,
  WebRTCProviderEventTypes,
  JanusErrorCodes
} from '@/webrtc/constants/eventTypes'
import { UserDisplayInfo, Participant } from '@/webrtc/types'
import { LoginUser } from '@/types/user'
import { FromUser } from '@/websocket/receivers/call/receiveMessages'

export type MediaComponentProps = Participant & {
  muted: boolean
  stream: MediaStream
  isLocal: boolean
}

@Component({
  components: {
    VideoCallRoomMaximum: () => import('./VideoCallRoomMaximum.vue'),
    VideoCallRoomMinumum: () => import('./VideoCallRoomMinimum.vue')
  }
})
export default class VideoCallRoomContainer extends Vue {
  @Prop(Object) user!: LoginUser
  @Prop(Boolean) isCaller!: boolean
  @Prop({ default: '' }) occupationCode!: string
  @Prop({ default: '' }) occupationShortName!: string

  $store!: ExStore
  showingCallError: number | null = null

  get inCallWebView() {
    return this.$store.getters['videoCallRoom/inCallWebView']
  }

  get participants() {
    return this.$store.getters['videoCallRoom/participants']
  }

  get mainScreenParticipant() {
    return this.$store.getters['videoCallRoom/mainScreenParticipant']
  }

  get caller() {
    return this.$store.getters['videoCallRoom/caller']
  }

  get isContainingPatient() {
    return this.$store.getters['videoCallRoom/isContainingPatient']
  }

  get microphoneActive() {
    return this.$store.getters['videoCallRoom/microphoneActive']
  }

  get videoActive() {
    return this.$store.getters['videoCallRoom/videoActive']
  }

  get shareScreenActive() {
    return this.$store.getters['videoCallRoom/shareScreenActive']
  }

  get userFullName() {
    return `${this.user.lastName}${this.user.firstName}`
  }

  get userInfo(): UserDisplayInfo {
    return {
      lastName: this.user.lastName,
      firstName: this.user.firstName,
      isPatient: this.user.isPatient,
      occupationCode: this.occupationCode,
      occupationShortName: this.occupationShortName
    }
  }

  get alertVisible() {
    return this.$store.getters['alert/visible']
  }

  async created() {
    const webrtc: WebRTC = await new Promise(resolve =>
      !WebRTCProvider.getConnection()
        ? WebRTCProvider.observeOnce(
            WebRTCProviderEventTypes.WEB_RTC_CREATED,
            resolve
          )
        : resolve(WebRTCProvider.getConnection() as WebRTC)
    )

    webrtc.observe(
      this.isCaller
        ? WebRTCEventTypes.ROOM_CREATED
        : WebRTCEventTypes.SESSION_SUCCEEDED,
      () => {
        webrtc.publish(this.user.id, this.userInfo)
      }
    )
    webrtc.observe(
      WebRTCEventTypes.LOCAL_STREAM,
      this.handleLocalStream.bind(this)
    )
    webrtc.observe(
      WebRTCEventTypes.REMOTE_STREAM,
      this.handleRemoteStream.bind(this)
    )
    webrtc.observe(
      WebRTCEventTypes.FORCE_SHARE_SCREEN_CHANGE,
      (state: boolean) =>
        this.$store.commit('videoCallRoom/shareScreenActive', state)
    )
    webrtc.observe(
      WebRTCEventTypes.REMOTE_LEAVED,
      this.handleRemoteLeaved.bind(this)
    )
    webrtc.observe(WebRTCEventTypes.SESSION_DESTROYED, () =>
      this.$emit('destroyed')
    )
    webrtc.observe(
      WebRTCEventTypes.DEVICE_LISTED,
      this.handleDeviceListed.bind(this)
    )
    webrtc.observe(WebRTCEventTypes.JANUS_ERROR, this.handleError.bind(this))
  }

  @Watch('microphoneActive')
  handleMuteAudio(value: boolean) {
    const webrtc = WebRTCProvider.getConnection()
    if (webrtc) {
      webrtc.switchAudio(value)
    }
  }

  @Watch('videoActive')
  handleMuteVideo(value: boolean) {
    const webrtc = WebRTCProvider.getConnection()
    if (webrtc) {
      webrtc.switchVideo(value)
    }
  }

  @Watch('shareScreenActive')
  handleShareScreen(value: boolean) {
    const webrtc = WebRTCProvider.getConnection()
    if (webrtc) {
      webrtc.switchShareScreen(value)
    }
  }

  @Watch('alertVisible')
  async handleAlertVisible(visible: boolean) {
    if (!visible && this.showingCallError === JanusErrorCodes.NO_SUCH_ROOM) {
      this.showingCallError = null
      WebRTCProvider.destroy()
      await this.$store.dispatch('videoCallRoom/leave')
      if (this.inCallWebView) {
        this.$router.replace({ name: 'Hangup' })
      }
    }
  }

  async handleHangUp() {
    WebRTCProvider.destroy()
  }

  handleChangeMainScreen(id: number) {
    this.$store.commit('videoCallRoom/setMainScreenParticipant', { id })
  }

  handleLocalStream(stream: MediaStream) {
    const muted = true

    this.$store.commit('videoCallRoom/addParticipants', {
      ...this.userInfo,
      stream,
      muted,
      id: this.user.id,
      isLocal: true
    })

    this.setSelectedInputMediaDevicesByStream(stream)
  }

  handleRemoteStream(participant: Participant, stream: MediaStream) {
    const muted = false

    this.$store.commit('videoCallRoom/addParticipants', {
      ...participant,
      stream,
      muted,
      isLocal: false
    })
    this.$store.commit('videoCallRoom/removePendingUser', {
      id: participant.id
    })

    if (this.isDefaultMainScreenUser(participant)) {
      this.$store.commit('videoCallRoom/setMainScreenParticipant', {
        id: participant.id
      })
    }
  }

  async handleError(errorCode: number) {
    if (errorCode === JanusErrorCodes.NO_SUCH_ROOM) {
      this.showingCallError = errorCode
      this.$store.dispatch('alert/showErrorMessages', ['通話に接続できません'])
    }
  }

  isDefaultMainScreenUser(participant: Participant) {
    if (participant.isPatient) {
      return true
    }

    if (
      !this.isContainingPatient &&
      !this.isCaller &&
      (this.caller as FromUser).id === Number(participant.id)
    ) {
      return true
    }

    if (this.isCaller && this.participants.length < 3) {
      return true
    }

    return false
  }

  handleRemoteLeaved(leavingUserId: number) {
    if (leavingUserId === this.mainScreenParticipant!.id) {
      if (this.participants.length > 2) {
        const { id } = this.participants.find(
          ({ id }) => id !== this.user.id && id !== leavingUserId
        ) as Participant
        this.$store.commit('videoCallRoom/setMainScreenParticipant', { id })
      } else {
        this.$store.commit('videoCallRoom/setMainScreenParticipant', {
          id: this.user.id
        })
      }
    }
    this.$store.commit('videoCallRoom/removeParticipants', {
      id: leavingUserId
    })
  }

  handleDeviceListed(devices: MediaDeviceInfo[]) {
    this.$store.commit('videoCallRoom/refrechMediaDevices', devices)
  }

  setSelectedInputMediaDevicesByStream(stream: MediaStream) {
    stream.getTracks().forEach(track => {
      const deviceSettings = track.getSettings()
      const type = `${track.kind}input` as MediaDeviceKind
      const id = deviceSettings.deviceId

      if (typeof id === 'undefined') {
        return
      }

      this.$store.commit('videoCallRoom/setSelectedMediaDevice', { type, id })
    })
  }
}
